// StaffHoursStat.js — Staff Working Hours (full redesign, 2026-08-04).
//
// Each ROLE gets its own section card — cleaners and drivers are different
// jobs measured differently, and mixing them in one grid made both harder to
// read. A section owns its header stats (total, average, top performer), its
// own hour-density heat scale, and its own footer of daily totals. One cell
// per person per day: duration leads, start–end rides beneath.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import './staffHoursStat.css';

function toDubaiDateISO(dateObj = new Date()) {
  return DateTime.fromJSDate(dateObj).setZone('Asia/Dubai').toISODate();
}

function startOfWeekMondayISO(isoDateStr) {
  const dt = DateTime.fromISO(isoDateStr, { zone: 'Asia/Dubai' }).startOf('day');
  return dt.minus({ days: dt.weekday - 1 }).toISODate(); // Mon=1 … Sun=7
}

function formatTimeHHMM(dateTimeStr) {
  if (!dateTimeStr) return '';
  const dt = DateTime.fromISO(dateTimeStr, { setZone: true }).setZone('Asia/Dubai');
  return dt.isValid ? dt.toFormat('HH:mm') : '';
}

/** 26520 → "7h 22m" — durations are for humans, nobody schedules in seconds. */
function formatDuration(totalSeconds) {
  const s = Math.max(0, Number(totalSeconds || 0));
  if (s === 0) return '0h';
  const hh = Math.floor(s / 3600);
  const mm = Math.round((s % 3600) / 60);
  if (hh === 0) return `${mm}m`;
  return mm === 0 ? `${hh}h` : `${hh}h ${String(mm).padStart(2, '0')}m`;
}

function isWeekendISO(isoDateStr) {
  const w = DateTime.fromISO(isoDateStr, { zone: 'Asia/Dubai' }).weekday;
  return w === 6 || w === 7;
}

function initialsOf(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
}

const ROLE_META = {
  cleaner: { label: 'Cleaners', tile: 'C', accent: 'coral' },
  driver: { label: 'Drivers', tile: 'D', accent: 'blue' },
};

/* ── One role's card: header stats + grid + footer ─────────────────────── */
function RoleSection({ roleKey, rows, weekDays, isCurrentWeek, dubaiTodayISO }) {
  const meta = ROLE_META[roleKey] || { label: roleKey, tile: '?', accent: 'coral' };

  const stats = useMemo(() => {
    const total = rows.reduce((s, r) => s + r.weekTotal, 0);
    const worked = rows.filter((r) => r.weekTotal > 0);
    const top = [...rows].sort((a, b) => b.weekTotal - a.weekTotal)[0] || null;
    const dayTotals = weekDays.map((_, i) => rows.reduce((s, r) => s + r.days[i].totalSeconds, 0));
    return {
      total,
      avg: worked.length ? total / worked.length : 0,
      top: top && top.weekTotal > 0 ? top : null,
      noDayOff: rows.filter((r) => !r.hasDayOff).length,
      dayTotals,
    };
  }, [rows, weekDays]);

  if (rows.length === 0) return null;

  return (
    <section className={`shs__section shs__section--${meta.accent}`}>
      <header className="shs__secHead">
        <div className="shs__secId">
          <span className="shs__secTile" aria-hidden="true">{meta.tile}</span>
          <div>
            <h3 className="shs__secTitle">{meta.label}</h3>
            <span className="shs__secCount">{rows.length} staff</span>
          </div>
        </div>

        <div className="shs__secStats">
          <div className="shs__secStat">
            <span className="shs__secStatVal">{formatDuration(stats.total)}</span>
            <span className="shs__secStatLbl">Total</span>
          </div>
          <div className="shs__secStat">
            <span className="shs__secStatVal">{formatDuration(stats.avg)}</span>
            <span className="shs__secStatLbl">Avg / staff</span>
          </div>
          {stats.top && (
            <div className="shs__secStat shs__secStat--top">
              <span className="shs__secStatVal">{stats.top.cleaner_name.split(' ')[0]}</span>
              <span className="shs__secStatLbl">Top · {formatDuration(stats.top.weekTotal)}</span>
            </div>
          )}
          {stats.noDayOff > 0 && (
            <div className="shs__secStat shs__secStat--warn">
              <span className="shs__secStatVal">{stats.noDayOff}</span>
              <span className="shs__secStatLbl">No day off</span>
            </div>
          )}
        </div>
      </header>

      <div className="shs__tableWrap">
        <table className="shs__table">
          <thead>
            <tr>
              <th className="shs__thName">Name</th>
              {weekDays.map((iso) => {
                const dt = DateTime.fromISO(iso, { zone: 'Asia/Dubai' });
                const cls = [
                  'shs__thDay',
                  isWeekendISO(iso) ? 'is-weekend' : '',
                  isCurrentWeek && iso === dubaiTodayISO ? 'is-today' : '',
                ].filter(Boolean).join(' ');
                return (
                  <th key={iso} className={cls}>
                    <span className="shs__thDow">{dt.toFormat('ccc')}</span>
                    <span className="shs__thDate">{dt.toFormat('d MMM')}</span>
                  </th>
                );
              })}
              <th className="shs__thTotal">Week</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.cleaner_id}>
                <td className="shs__tdName">
                  <div className="shs__nameWrap">
                    <span className="shs__avatar">{initialsOf(r.cleaner_name)}</span>
                    <span className="shs__name" title={r.cleaner_name}>{r.cleaner_name}</span>
                    {/* Quiet amber dot — the loud per-row NO DAY OFF badges
                        made the whole grid shout (). */}
                    {!r.hasDayOff && (
                      <span className="shs__noOffDot" title="No day off yet this week" />
                    )}
                  </div>
                </td>

                {r.days.map((d) => {
                  const cls = [
                    'shs__tdDay',
                    d.isWeekend ? 'is-weekend' : '',
                    d.isToday ? 'is-today' : '',
                    d.isFuture ? 'is-future' : '',
                    d.isOff ? 'is-off' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <td key={d.iso} className={cls}>
                      {d.isFuture ? (
                        <span className="shs__dash">–</span>
                      ) : d.isOff ? (
                        <span className="shs__off">Off</span>
                      ) : (
                        <>
                          <span className="shs__dur">{formatDuration(d.totalSeconds)}</span>
                          <span className="shs__times">
                            {formatTimeHHMM(d.start)}–{formatTimeHHMM(d.end)}
                          </span>
                        </>
                      )}
                    </td>
                  );
                })}

                <td className="shs__tdTotal">
                  <span className="shs__weekDur">{formatDuration(r.weekTotal)}</span>
                  <span className="shs__weekDays">{r.daysWorked}/7 days</span>
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <td className="shs__tfLabel">Daily total</td>
              {stats.dayTotals.map((secs, i) => {
                // A future day's rows show a dash, so its total must too —
                // planned hours are not worked hours.
                const isFuture = isCurrentWeek && weekDays[i] > dubaiTodayISO;
                return (
                  <td
                    key={weekDays[i]}
                    className={`shs__tfDay ${isWeekendISO(weekDays[i]) ? 'is-weekend' : ''}`}
                  >
                    {!isFuture && secs > 0 ? formatDuration(secs) : '–'}
                  </td>
                );
              })}
              <td className="shs__tfTotal">{formatDuration(stats.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */
export default function StaffHoursStat() {
  const dubaiTodayISO = useMemo(() => toDubaiDateISO(new Date()), []);
  const currentWeekStartISO = useMemo(() => startOfWeekMondayISO(dubaiTodayISO), [dubaiTodayISO]);

  const [weekStart, setWeekStart] = useState(() => currentWeekStartISO);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [staff, setStaff] = useState([]);
  const [sortByHours, setSortByHours] = useState(false);

  const isCurrentWeek = weekStart === currentWeekStartISO;

  const weekDays = useMemo(() => {
    const base = DateTime.fromISO(weekStart, { zone: 'Asia/Dubai' }).startOf('day');
    return Array.from({ length: 7 }, (_, i) => base.plus({ days: i }).toISODate());
  }, [weekStart]);

  const weekLabel = useMemo(() => {
    const a = DateTime.fromISO(weekDays[0], { zone: 'Asia/Dubai' });
    const b = DateTime.fromISO(weekDays[6], { zone: 'Asia/Dubai' });
    const sameMonth = a.month === b.month;
    return `${a.toFormat('d')}${sameMonth ? '' : a.toFormat(' MMM')} – ${b.toFormat('d MMM yyyy')}`;
  }, [weekDays]);

  const fetchData = async (ws = weekStart) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/statistics/cleaner-hours', {
        params: { week_start: ws },
        withCredentials: true,
      });
      const list = Array.isArray(res.data?.staff)
        ? res.data.staff
        : Array.isArray(res.data?.cleaners) ? res.data.cleaners : [];
      setStaff(list);
    } catch (e) {
      console.error('Failed to load staff hours', e);
      setError('Failed to load staff hours.');
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(weekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const shiftWeek = (deltaWeeks) => {
    setWeekStart((ws) =>
      DateTime.fromISO(ws, { zone: 'Asia/Dubai' }).plus({ weeks: deltaWeeks }).toISODate()
    );
  };

  const rows = useMemo(() => {
    const mapped = staff.map((c) => {
      const byDay = new Map((c.days || []).map((d) => [d.day_date, d]));
      const days = weekDays.map((iso) => {
        const row = byDay.get(iso) || null;
        const total = row ? Number(row.total_seconds || 0) : 0;
        const isFuture = isCurrentWeek && iso > dubaiTodayISO;
        return {
          iso,
          start: row?.day_start || null,
          end: row?.day_end || null,
          totalSeconds: total,
          isWeekend: isWeekendISO(iso),
          isToday: isCurrentWeek && iso === dubaiTodayISO,
          isOff: total === 0 && !isFuture,
          isFuture,
        };
      });
      // Week total is summed from the days ACTUALLY WORKED so far — the
      // backend's week_total_seconds includes planned future bookings, which
      // made the Week column disagree with the visible cells.
      const workedDays = days.filter((d) => !d.isFuture && d.totalSeconds > 0);
      return {
        cleaner_id: c.cleaner_id,
        cleaner_name: c.cleaner_name,
        role: c.role === 'driver' ? 'driver' : 'cleaner',
        days,
        weekTotal: workedDays.reduce((s, d) => s + d.totalSeconds, 0),
        daysWorked: workedDays.length,
        hasDayOff: days.some((d) => d.isOff),
      };
    });
    return sortByHours ? [...mapped].sort((a, b) => b.weekTotal - a.weekTotal) : mapped;
  }, [staff, weekDays, isCurrentWeek, dubaiTodayISO, sortByHours]);

  const byRole = useMemo(() => ({
    cleaner: rows.filter((r) => r.role === 'cleaner'),
    driver: rows.filter((r) => r.role === 'driver'),
  }), [rows]);

  return (
    <div className="shs">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="shs__toolbar">
        <div className="shs__toolbarLeft">
          <h2 className="shs__title">Staff Working Hours</h2>
          <p className="shs__hint">
            Cleaners: first booking start → last report upload − 5 min · Drivers: clock on/off · Dubai time
          </p>
        </div>

        <div className="shs__toolbarRight">
          <button
            type="button"
            className={`shs__sortBtn ${sortByHours ? 'is-on' : ''}`}
            onClick={() => setSortByHours((v) => !v)}
            title="Toggle between alphabetical and most-hours-first"
          >
            {sortByHours ? 'Most hours first' : 'A–Z'}
          </button>

          <div className="shs__weekNav">
            <button type="button" className="shs__navBtn" onClick={() => shiftWeek(-1)} aria-label="Previous week">‹</button>
            <div className="shs__weekLabel">
              <span className="shs__weekRange">{weekLabel}</span>
              {isCurrentWeek
                ? <span className="shs__thisWeek">This week</span>
                : (
                  <button type="button" className="shs__todayLink" onClick={() => setWeekStart(currentWeekStartISO)}>
                    Back to this week
                  </button>
                )}
            </div>
            <button type="button" className="shs__navBtn" onClick={() => shiftWeek(1)} aria-label="Next week">›</button>
          </div>

          <button type="button" className="shs__refreshBtn" onClick={() => fetchData()} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="shs__error">{error}</div>}
      {!error && !loading && rows.length === 0 && (
        <div className="shs__empty">No staff hours for this week.</div>
      )}

      <RoleSection
        roleKey="cleaner"
        rows={byRole.cleaner}
        weekDays={weekDays}
        isCurrentWeek={isCurrentWeek}
        dubaiTodayISO={dubaiTodayISO}
      />
      <RoleSection
        roleKey="driver"
        rows={byRole.driver}
        weekDays={weekDays}
        isCurrentWeek={isCurrentWeek}
        dubaiTodayISO={dubaiTodayISO}
      />
    </div>
  );
}
